'use client';

import React, { useState } from 'react';
import { calculateFlames, FlamesResult } from '@/lib/api/client';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { User, Gamepad2 } from 'lucide-react';

export default function FlamesCalculatorPage() {
  const [nameA, setNameA] = useState('Rahul');
  const [nameB, setNameB] = useState('Anjali');
  const [result, setResult] = useState<FlamesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await calculateFlames(nameA, nameB);
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate FLAMES result');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "FLAMES is a popular nostalgic childhood name game used for lighthearted entertainment. It counts common letters in two names to map them to the letters F-L-A-M-E-S (Friends, Lovers, Affectionate, Marriage, Enemies, Siblings).";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I calculate the FLAMES result by canceling matching letters between the two names, counting the remaining unmatched letter length, and counting iteratively through the 6 FLAMES letters until one stands remaining.
      </p>
      <p className="italic text-[#ffd36b] text-xs">
        Note: FLAMES is an informal party game and not part of classical Vedic astrology or Ashtakoot Kundli matching.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "The FLAMES Acronym Meaning",
      content: "F = Friends, L = Love, A = Affection, M = Marriage, E = Enemies, S = Siblings. Each letter represents one of six classic playful categories."
    },
    {
      title: "Letter Cancellation Logic",
      content: "Common letters in both names are crossed out. The remaining count determines how many steps to count around the 6-letter FLAMES ring."
    },
    {
      title: "Vedic Matching vs FLAMES Game",
      content: "For serious relationship analysis, we recommend using 36-Point Ashtakoot Guna Milan (Kundli Matching) based on actual planetary birth coordinates."
    }
  ];

  const faqs = [
    {
      q: "What does FLAMES stand for?",
      a: "FLAMES stands for Friends, Love, Affection, Marriage, Enemies, and Siblings."
    },
    {
      q: "Is FLAMES based on Vedic astrology?",
      a: "No. FLAMES is a nostalgic recreational name game, not a Vedic astronomical calculation."
    },
    {
      q: "How can I check real astrological compatibility?",
      a: "Use our Kundli Matching tool for 36-Point Ashtakoot Guna Milan or Zodiac Compatibility for Western Sun sign analysis."
    },
    {
      q: "Is this FLAMES Calculator free?",
      a: "Yes, 100%! All games and calculators on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Kundli Matching (36 Guna)",
      href: "/kundli-matching",
      description: "Serious Vedic Ashtakoot Guna Milan for marriage matching.",
      categoryBadge: "Vedic"
    },
    {
      title: "Zodiac Compatibility",
      href: "/compatibility",
      description: "Explore Western Sun sign pair compatibility.",
      categoryBadge: "Western"
    },
    {
      title: "Name Numerology Calculator",
      href: "/name-numerology-calculator",
      description: "Analyze name acoustic vibrations using Pythagorean numerology.",
      categoryBadge: "Numerology"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      {/* Playful Header Banner */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden text-center sm:text-left">
        <div className="flex items-center justify-center sm:justify-start gap-2 text-[#ff9bb0] text-xs font-bold uppercase tracking-wider mb-2">
          <Gamepad2 className="w-4 h-4 text-[#ff9bb0]" />
          <span>Nostalgic Name Matching Game</span>
          <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-[#b9c6ff] border border-white/15 text-[10px]">Just for Fun</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-2">
          FLAMES Name Calculator
        </h1>
        <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm max-w-2xl leading-[1.6]">
          Enter two names to test the classic FLAMES (Friends, Love, Affection, Marriage, Enemies, Siblings) letter cancellation game!
        </p>
      </div>

      {/* Input Form */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="flames-name-a" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center">
                <User className="w-3.5 h-3.5 inline mr-1.5 text-[#ff9bb0]" />
                First Name
              </label>
              <input
                id="flames-name-a"
                type="text"
                required
                value={nameA}
                onChange={(e) => setNameA(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
              />
            </div>
            <div>
              <label htmlFor="flames-name-b" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 flex items-center">
                <User className="w-3.5 h-3.5 inline mr-1.5 text-[#ff9bb0]" />
                Second Name
              </label>
              <input
                id="flames-name-b"
                type="text"
                required
                value={nameB}
                onChange={(e) => setNameB(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] transition-all disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
          >
            {loading ? 'Testing FLAMES...' : 'Test FLAMES Match'}
          </button>
        </form>
        {error && <p className="mt-3 text-xs text-[#ff9bb0]">{error}</p>}
      </div>

      {/* Results Section */}
      {result && (
        <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl space-y-3">
          <h2 className="text-xl font-heading font-semibold text-white">
            FLAMES Result: <span className="text-[#b9c6ff]">{result.meaning}</span>
          </h2>
          <p className="text-xs sm:text-sm text-[rgba(220,236,255,0.85)] leading-[1.6]">{result.description}</p>
        </div>
      )}

      <CalculatorPageContent
        calculatorName="FLAMES Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How FLAMES Letter Cancellation Works"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
